import { PageHeader, useExport } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, EditPane, Field, Select } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LABEL_SHEET_TYPES, useApi, type LabelSheetType, type OrderNoteDTO } from "../../api";
import { FINANCIAL_VARIANT, FULFILLMENT_VARIANT, ORDER_KIND_VARIANT } from "../../components/badges";
import { crudEditor, textField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";
import { ProductionJobTree } from "../production/ProductionJobTree";

/**
 * One order: its production jobs and its internal notes.
 *
 * Notes are keyed by (`order_type`, `order_id`) rather than a foreign key,
 * because a note can hang off either an order or a draft order — the same
 * polymorphic pairing the Vaadin grid wrote.
 */
export function OrderDetailPage() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const { start: startExport } = useExport();
  const { id } = useParams({ from: "/orders/$id" });
  const orderHeaderId = Number(id);
  const allowed = useAllowed(LogicView.ORDERS);
  const title = t("navigation.item.orders", "Orders");

  const [cardJobId, setCardJobId] = useState<number | null>(null);
  const [labelSheet, setLabelSheet] = useState<LabelSheetType>("OL75WX");

  const order = useQuery({
    queryKey: ["order", orderHeaderId],
    queryFn: () => api.orders.findById(orderHeaderId),
    enabled: allowed && Number.isFinite(orderHeaderId),
  });

  const notes = useQuery({
    queryKey: ["order", orderHeaderId, "notes"],
    queryFn: () => api.orderNotes.getByOrder("ORDER", orderHeaderId),
    enabled: allowed && Number.isFinite(orderHeaderId),
  });

  const refreshNotes = () =>
    queryClient.invalidateQueries({ queryKey: ["order", orderHeaderId, "notes"] });

  const noteColumns = useMemo(() => {
    const c = defineColumns<OrderNoteDTO>();
    return [c.text("note", (row) => row.note, { headerKey: "column.note", priority: 1 })];
  }, []);

  const noteEditor = useMemo(
    () =>
      crudEditor<OrderNoteDTO>({
        client: api.orderNotes,
        refresh: refreshNotes,
        t,
        fields: {
          note: textField(
            (row) => row.note,
            (row, value) => ({ ...row, note: value }),
            true,
          ),
        },
        newRow: () => ({
          id: 0,
          order_type: "ORDER",
          order_id: orderHeaderId,
          account_id: 0,
          note: "",
        }),
        validate: (row) =>
          row.note.trim() ? null : { note: t("form.validation.field.required", "Value is required") },
      }),
    [api, orderHeaderId, t],
  );

  if (!allowed) return <AccessDenied title={title} />;
  if (order.error) return <ErrorState title={title} error={order.error} />;

  const entity = order.data;
  const shopify = entity?.order;

  return (
    <>
      <PageHeader
        parent={{ to: "/orders", label: t("navigation.item.orders", "Orders") }}
        title={`${entity?.order_no ?? `#${orderHeaderId}`}`}
        badges={
          shopify ? (
            <div className="flex items-center gap-1.5">
              <Badge variant={ORDER_KIND_VARIANT.ORDER}>ORDER</Badge>
              <Badge
                variant={
                  FULFILLMENT_VARIANT[
                    (shopify.displayFulfillmentStatus ?? "UNFULFILLED") as keyof typeof FULFILLMENT_VARIANT
                  ] ?? "neutral"
                }
              >
                {shopify.displayFulfillmentStatus ?? "UNFULFILLED"}
              </Badge>
              <Badge
                variant={
                  FINANCIAL_VARIANT[
                    (shopify.displayFinancialStatus ?? "PENDING") as keyof typeof FINANCIAL_VARIANT
                  ] ?? "neutral"
                }
              >
                {shopify.displayFinancialStatus ?? "PENDING"}
              </Badge>
            </div>
          ) : undefined
        }
      />

      <EditPane
        tabs={[
          {
            id: "production",
            flush: true,
            label: t("tab.production-job", "Production job"),
            content: (
              <ProductionJobTree
                orderHeaderId={orderHeaderId}
                onPrintJobCard={(jobId) => setCardJobId(jobId)}
              />
            ),
          },
          {
            id: "notes",
            flush: true,
            label: t("tab.notes", "Notes"),
            badge: <Badge size="sm">{notes.data?.length ?? 0}</Badge>,
            content: (
              <DataTable<OrderNoteDTO>
                data={notes.data ?? []}
                columns={noteColumns}
                getRowId={(row) => String(row.id)}
                loading={notes.isLoading}
                editor={noteEditor}
              />
            ),
          },
        ]}
      />

      <Dialog
        open={cardJobId !== null}
        onClose={() => setCardJobId(null)}
        title={t("dialog.print-job-card.title", "Print job card")}
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => setCardJobId(null)}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (cardJobId === null) return;
                void startExport(`job-card-${cardJobId}`, () =>
                  api.productionJobs.getJobCard(cardJobId, labelSheet),
                );
                setCardJobId(null);
              }}
            >
              {t("dialog.confirm.print", "Print")}
            </Button>
          </>
        }
      >
        <Field label={t("form.field.label-sheet-type", "Label sheet type")} required>
          <Select
            value={labelSheet}
            options={LABEL_SHEET_TYPES.map((entry) => ({ value: entry, label: entry }))}
            onChange={(event) => setLabelSheet(event.target.value as LabelSheetType)}
          />
        </Field>
      </Dialog>
    </>
  );
}
